"use client";

import { useState, useEffect } from "react";
import useSWRMutation from "swr/mutation";
import { isEmailAddress, isValidName } from "@/app/utils/validation";
import logger from "@/app/utils/clientLogger";

interface NewsletterArg {
  emailAddress: string;
  name?: string;
  hp?: string;
}

async function postNewsletter(url: string, { arg }: { arg: NewsletterArg }) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(arg),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Request failed");
  }
  return res.json();
}

export default function JoinNewsletterForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState("");
  const [fadingOut, setFadingOut] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { trigger, isMutating } = useSWRMutation("/api/newsletter", postNewsletter);

  useEffect(() => {
    if (fadingOut) {
      const timer = setTimeout(() => setShowSuccess(true), 400);
      return () => clearTimeout(timer);
    }
  }, [fadingOut]);

  // Returns the first validation error, or null if all fields are valid.
  function validate(): string | null {
    if (!isValidName(name)) {
      return "Please enter your full name (at least 5 characters, including a space).";
    }
    if (!isEmailAddress(email)) {
      return "Please enter a valid email address.";
    }
    return null;
  }

  async function handleSubmit() {
    setErrorMessage("");

    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      await trigger({ emailAddress: email.trim(), name: name.trim(), hp });
      setFadingOut(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setErrorMessage(message);
      logger.error("Newsletter signup failed", err);
    }
  }

  return (
    <div className="w-full max-w-md px-4 pb-6 flex flex-col items-center gap-3">
      {showSuccess ? (
        <p className="text-sm text-center text-success animate-fade-in">
          Thanks for submitting your details, keep an eye out for updates!
        </p>
      ) : (
        <>
          <div
            className={`flex flex-col w-full gap-2 transition-opacity duration-400 ${fadingOut ? "opacity-0" : "opacity-100"}`}
          >
            <input
              type="text"
              placeholder="Your name"
              className="input input-bordered w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isMutating) handleSubmit();
              }}
              required
            />
            <div className="flex w-full gap-2">
              <input
                type="email"
                placeholder="Your email address"
                className="input input-bordered flex-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isMutating) handleSubmit();
                }}
              />
              <button
                className="btn btn-primary"
                disabled={isMutating}
                onClick={handleSubmit}
              >
                {isMutating && <span className="loading loading-spinner loading-sm" />}
                {isMutating ? "Sending..." : "Get updates"}
              </button>
            </div>
            {/* Honeypot field - hidden from real users to trap spam bots */}
            <div className="hidden" aria-hidden="true">
              <label htmlFor="hp">Leave this field empty</label>
              <input
                type="text"
                id="hp"
                tabIndex={-1}
                autoComplete="off"
                value={hp}
                onChange={(e) => setHp(e.target.value)}
              />
            </div>
          </div>
          {errorMessage ? (
            <p className="text-sm text-center text-error">{errorMessage}</p>
          ) : (
            <p className="text-xs text-center opacity-70">
              Signing up subscribes you to our newsletter for new episode
              updates, special offers and more - we don&apos;t share your details
              and you can unsubscribe at any time.
            </p>
          )}
        </>
      )}
    </div>
  );
}
